// 有料生成ジョブのコストを、表示用ログと構造化結果の双方から復元する純粋関数群。
// OpenRouter は API 応答の usage.cost（実費USD）、Meshy は consumed_credits（実消費P）を正とする。

export const GENERATION_COST_FORMAT_VERSION = '1.0';
// Meshy API はUSDを返さないため、公式Pro月額 $20 / 1,000 credits の基準を固定保存する。
// 実消費 credits も必ず併記し、将来の単価変更後も当時の換算根拠を失わない。
export const DEFAULT_MESHY_USD_PER_CREDIT = 0.02;
export const DEFAULT_MESHY_USD_RATE_BASIS = 'Meshy Pro monthly $20 / 1,000 credits (2026-07)';

const roundMoney = (value) => Math.round((Number(value) + Number.EPSILON) * 1_000_000) / 1_000_000;
const finiteNumber = (value) => value === null || value === undefined || value === ''
    ? null
    : (Number.isFinite(Number(value)) ? Number(value) : null);
const stringArray = (value) => Array.isArray(value) ? value.map((line) => String(line)) : [];

function matchesFromLines(lines, pattern) {
    const values = [];
    for (const line of stringArray(lines)) {
        pattern.lastIndex = 0;
        let match;
        while ((match = pattern.exec(line))) {
            const value = Number(match[1]);
            if (Number.isFinite(value)) values.push(value);
            if (!pattern.global) break;
        }
    }
    return values;
}

/** OpenRouter の1呼び出しごとの実費行を優先し、旧ログでそれが無い場合だけジョブ合計行を使う。 */
export function openRouterCostFromLines(lines) {
    const source = stringArray(lines);
    const calls = matchesFromLines(source, /(?:^|\s)(?:💰\s*)?実費:\s*\$([0-9]+(?:\.[0-9]+)?)/g);
    const totals = calls.length > 0
        ? []
        : matchesFromLines(source, /実費合計:\s*\$([0-9]+(?:\.[0-9]+)?)/g);
    const values = calls.length > 0 ? calls : totals;
    const unknownCalls = source.filter((line) => /(?:💰\s*)?実費:\s*不明/.test(line)).length;
    return {
        usd: roundMoney(values.reduce((sum, value) => sum + value, 0)),
        calls: calls.length || totals.length,
        unknownCalls,
        evidence: values.length > 0 || unknownCalls > 0,
    };
}

/** Meshy はジョブ合計行を優先し、キャスト別ログのように合計が無い場合は工程別消費行を加算する。 */
export function meshyCreditsFromLines(lines) {
    const source = stringArray(lines);
    const totals = matchesFromLines(source, /Meshy\s*消費合計\s*([0-9]+(?:\.[0-9]+)?)\s*P/g);
    const stages = totals.length > 0
        ? []
        : matchesFromLines(source, /Meshy\s*消費:\s*([0-9]+(?:\.[0-9]+)?)\s*P/g);
    const values = totals.length > 0 ? totals : stages;
    return {
        credits: roundMoney(values.reduce((sum, value) => sum + value, 0)),
        evidence: values.length > 0,
    };
}

function normalizeSubtask(id, value, fallbackLines = []) {
    const derived = summarizeCostLines(fallbackLines);
    const openRouterUsd = finiteNumber(value?.openRouterUsd) ?? derived?.openRouterUsd ?? 0;
    const meshyCredits = finiteNumber(value?.meshyCredits) ?? derived?.meshyCredits ?? 0;
    const meshyUsdPerCredit = finiteNumber(value?.meshyUsdPerCredit)
        ?? derived?.meshyUsdPerCredit ?? DEFAULT_MESHY_USD_PER_CREDIT;
    const meshyUsd = finiteNumber(value?.meshyUsd) ?? roundMoney(meshyCredits * meshyUsdPerCredit);
    const totalUsd = finiteNumber(value?.totalUsd) ?? roundMoney(openRouterUsd + meshyUsd);
    return {
        id: String(value?.id ?? id),
        ...(typeof value?.label === 'string' && value.label ? { label: value.label } : {}),
        totalUsd,
        openRouterUsd: roundMoney(openRouterUsd),
        meshyUsd: roundMoney(meshyUsd),
        meshyCredits: roundMoney(meshyCredits),
        meshyUsdPerCredit,
        ...(Number.isFinite(Number(value?.openRouterCalls)) ? { openRouterCalls: Number(value.openRouterCalls) } : {}),
        ...(Number.isFinite(Number(value?.openRouterUnknownCalls)) ? { openRouterUnknownCalls: Number(value.openRouterUnknownCalls) } : {}),
    };
}

/** ログ行だけから後方互換で工程合計を復元する。コスト情報が無い場合は null。 */
export function summarizeCostLines(lines, { meshyCredits = null, meshyUsdPerCredit = DEFAULT_MESHY_USD_PER_CREDIT } = {}) {
    const openRouter = openRouterCostFromLines(lines);
    const fromLines = meshyCreditsFromLines(lines);
    const explicitCredits = finiteNumber(meshyCredits);
    const credits = explicitCredits ?? fromLines.credits;
    const hasMeshy = explicitCredits !== null || fromLines.evidence;
    if (!openRouter.evidence && !hasMeshy) return null;
    const rate = finiteNumber(meshyUsdPerCredit) ?? DEFAULT_MESHY_USD_PER_CREDIT;
    const meshyUsd = roundMoney(credits * rate);
    return {
        formatVersion: GENERATION_COST_FORMAT_VERSION,
        currency: 'USD',
        totalUsd: roundMoney(openRouter.usd + meshyUsd),
        openRouterUsd: openRouter.usd,
        openRouterCalls: openRouter.calls,
        openRouterUnknownCalls: openRouter.unknownCalls,
        meshyUsd,
        meshyCredits: credits,
        meshyUsdPerCredit: rate,
        meshyUsdRateBasis: DEFAULT_MESHY_USD_RATE_BASIS,
        subtasks: {},
    };
}

function jsonUnitSubtasks(result) {
    if (!result?.units || typeof result.units !== 'object' || Array.isArray(result.units)) return {};
    const out = {};
    for (const [unitId, entry] of Object.entries(result.units)) {
        if (!entry || typeof entry !== 'object') continue;
        const summary = normalizeCostSummary(entry.costSummary, []);
        if (summary) out[unitId] = normalizeSubtask(unitId, { ...summary, id: unitId, label: unitId });
    }
    return out;
}

/** ジョブ完了時に、工程合計とキャスト／JSONユニット別の合計を構造化する。 */
export function summarizeJobCost(kind, result, lines) {
    const explicitMeshyCredits = kind === 'story-model-generate' ? finiteNumber(result?.creditsTotal) : null;
    const total = summarizeCostLines(lines, { meshyCredits: explicitMeshyCredits });
    if (!total) return null;
    const subtasks = {};
    if (result?.castLogs && typeof result.castLogs === 'object' && !Array.isArray(result.castLogs)) {
        for (const [castId, castLines] of Object.entries(result.castLogs)) {
            const castSummary = summarizeCostLines(castLines);
            if (castSummary) subtasks[castId] = normalizeSubtask(castId, { ...castSummary, id: castId });
        }
    }
    Object.assign(subtasks, jsonUnitSubtasks(result));
    return { ...total, subtasks };
}

/** 保存済み構造を検証し、不足項目は旧ログから復元する。 */
export function normalizeCostSummary(value, lines = [], { kind = '', result = null, castLogs = null } = {}) {
    const derived = summarizeJobCost(kind, result ?? (castLogs ? { castLogs } : null), lines);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return derived;
    const openRouterUsd = finiteNumber(value.openRouterUsd) ?? derived?.openRouterUsd ?? 0;
    const meshyCredits = finiteNumber(value.meshyCredits) ?? derived?.meshyCredits ?? 0;
    const rate = finiteNumber(value.meshyUsdPerCredit) ?? derived?.meshyUsdPerCredit ?? DEFAULT_MESHY_USD_PER_CREDIT;
    const meshyUsd = finiteNumber(value.meshyUsd) ?? roundMoney(meshyCredits * rate);
    const totalUsd = finiteNumber(value.totalUsd) ?? roundMoney(openRouterUsd + meshyUsd);
    const rawSubtasks = value.subtasks && typeof value.subtasks === 'object' && !Array.isArray(value.subtasks)
        ? value.subtasks : (derived?.subtasks ?? {});
    const subtasks = Object.fromEntries(Object.entries(rawSubtasks).map(([id, subtask]) => [
        id,
        normalizeSubtask(id, subtask, castLogs?.[id] ?? []),
    ]));
    return {
        formatVersion: GENERATION_COST_FORMAT_VERSION,
        currency: 'USD',
        totalUsd: roundMoney(totalUsd),
        openRouterUsd: roundMoney(openRouterUsd),
        openRouterCalls: Number.isFinite(Number(value.openRouterCalls)) ? Number(value.openRouterCalls) : (derived?.openRouterCalls ?? 0),
        openRouterUnknownCalls: Number.isFinite(Number(value.openRouterUnknownCalls)) ? Number(value.openRouterUnknownCalls) : (derived?.openRouterUnknownCalls ?? 0),
        meshyUsd: roundMoney(meshyUsd),
        meshyCredits: roundMoney(meshyCredits),
        meshyUsdPerCredit: rate,
        meshyUsdRateBasis: typeof value.meshyUsdRateBasis === 'string'
            ? value.meshyUsdRateBasis : (derived?.meshyUsdRateBasis ?? DEFAULT_MESHY_USD_RATE_BASIS),
        subtasks,
    };
}

/** 旧 generation-logs.json を書き換えず、プロジェクト詳細応答上でコスト付きへ補完する。 */
export function enrichGenerationLogStore(store) {
    if (!store || !Array.isArray(store.runs)) return { formatVersion: '1.1', runs: [] };
    return {
        formatVersion: '1.1',
        runs: store.runs.map((run) => {
            const costSummary = normalizeCostSummary(run?.costSummary, run?.lines, {
                kind: run?.kind,
                castLogs: run?.castLogs,
            });
            return { ...run, ...(costSummary ? { costSummary } : {}) };
        }),
    };
}

export function formatOpenRouterJobCostLine(lines, { elapsedSec = null } = {}) {
    const cost = openRouterCostFromLines(lines);
    if (!cost.evidence || cost.usd <= 0) return null;
    const usd = `$${cost.usd.toFixed(cost.usd < 0.001 ? 6 : 4)}`;
    const time = elapsedSec !== null ? `・処理時間 ${Number(elapsedSec).toFixed(1)}秒` : '';
    return `━ 実費合計: ${usd}（OpenRouter・${cost.calls} 呼び出し${time}）`;
}
